import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import RefreshIcon from '@mui/icons-material/Refresh'
import AssignmentIcon from '@mui/icons-material/Assignment'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import StatCard from '../../Shared/StatCard'
import DataTable from '../../Shared/DataTable'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { getDashboardSummary } from '../../../services/dashboardServices'
import { getAllBreaks } from '../../../services/productionOrderServices'
import { mensagemDeErro } from '../../../services/apiClient'
import { getOrderStatus } from '../../../const/productionStatus'
import { formatDate, formatDateTime, formatDuration, formatNumber } from '../../../utils/format'

const MOTIVO_PADRAO = 'Indicador sem origem de dados disponível na API.'

/**
 * Indicador que respeita `unavailable[]`: valor `null` vira "—" com o motivo
 * devolvido pela API no Tooltip. Nunca um número inventado.
 */
const IndicatorCard = ({ label, value, icon, color, loading, motivo }) => {
  const indisponivel = value === null || value === undefined

  const card = (
    <StatCard
      label={label}
      value={indisponivel ? '—' : value}
      icon={icon}
      color={color}
      hint={indisponivel ? 'Indisponível' : undefined}
      loading={loading}
    />
  )

  if (!indisponivel) return card

  return (
    <Tooltip title={motivo || MOTIVO_PADRAO}>
      <Box sx={{ height: '100%' }}>{card}</Box>
    </Tooltip>
  )
}

/** Data local em `YYYY-MM-DD` (o formato do input type="date" e da API). */
const paraDataLocal = data => {
  const ano = data.getFullYear()
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}

/** O painel abre nos últimos 30 dias (fim = hoje, início = 30 dias atrás). */
const inicioPadrao = () => {
  const data = new Date()
  data.setDate(data.getDate() - 30)
  return paraDataLocal(data)
}
const fimPadrao = () => paraDataLocal(new Date())

/** Tempo de produção da ordem = conclusão − início. "—" quando não concluída. */
const tempoDeProducao = row => {
  if (!row.startedAt || !row.finishedAt) return '—'
  const minutos = (new Date(row.finishedAt).getTime() - new Date(row.startedAt).getTime()) / 60000
  if (Number.isNaN(minutos) || minutos < 0) return '—'
  return formatDuration(Math.round(minutos))
}

const Dashboard = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [startDate, setStartDate] = useState(inicioPadrao)
  const [finishDate, setFinishDate] = useState(fimPadrao)
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)

  // Tabela de movimentações paginada (todas as ordens do período), separada do
  // resumo para poder navegar sem recarregar os indicadores.
  const [movimentacoes, setMovimentacoes] = useState([])
  const [movTotal, setMovTotal] = useState(0)
  const [movPage, setMovPage] = useState(0)
  const [movRowsPerPage, setMovRowsPerPage] = useState(10)
  const [loadingMov, setLoadingMov] = useState(false)

  const comPeriodo = Boolean(startDate || finishDate)

  const fetchSummary = useCallback(async () => {
    if (!subCompanyId) return
    setLoading(true)
    try {
      const response = await getDashboardSummary(subCompanyId, { startDate, finishDate })
      setSummary(response.data || null)
    } catch (error) {
      setSummary(null)
      notify(mensagemDeErro(error, 'Não foi possível carregar os indicadores.'), 'error')
    } finally {
      setLoading(false)
    }
  }, [subCompanyId, startDate, finishDate, notify])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  const fetchMovimentacoes = useCallback(async () => {
    if (!subCompanyId) {
      setMovimentacoes([])
      setMovTotal(0)
      return
    }
    setLoadingMov(true)
    try {
      const response = await getAllBreaks({
        subCompanyId,
        dateFrom: startDate,
        dateTo: finishDate,
        limit: movRowsPerPage,
        offset: movPage * movRowsPerPage,
      })
      setMovimentacoes(response.data.result || [])
      setMovTotal(response.data.total || 0)
    } catch (error) {
      setMovimentacoes([])
      setMovTotal(0)
      notify(mensagemDeErro(error, 'Não foi possível carregar as movimentações.'), 'error')
    } finally {
      setLoadingMov(false)
    }
  }, [subCompanyId, startDate, finishDate, movPage, movRowsPerPage, notify])

  useEffect(() => {
    fetchMovimentacoes()
  }, [fetchMovimentacoes])

  // Trocar de filial ou período volta a tabela para a primeira página.
  useEffect(() => {
    setMovPage(0)
  }, [subCompanyId, startDate, finishDate])

  /** Texto do recorte, com um só limite ou os dois. */
  const periodoAplicado = useMemo(() => {
    if (startDate && finishDate) {
      return `Período: ${formatDate(startDate)} a ${formatDate(finishDate)}`
    }
    if (startDate) return `Período: a partir de ${formatDate(startDate)}`
    if (finishDate) return `Período: até ${formatDate(finishDate)}`
    return ''
  }, [startDate, finishDate])

  /** `unavailable: [{ indicator, reason }]` vira um mapa indicador → motivo. */
  const motivos = useMemo(() => {
    const lista = (summary && summary.unavailable) || []
    return lista.reduce((mapa, item) => ({ ...mapa, [item.indicator]: item.reason }), {})
  }, [summary])

  const orders = (summary && summary.orders) || {}
  const rfid = (summary && summary.rfid) || {}

  // 'peca' = apontamentos por etapa; 'quebra' = quebras distintas (a OP quebrada).
  const [stageMetric, setStageMetric] = useState('peca')
  const stageData = useMemo(() => {
    if (!summary) return []
    return (stageMetric === 'quebra' ? summary.stagesByBreak : summary.stages) || []
  }, [summary, stageMetric])
  const stagesIndisponiveis = stageData.length === 0


  const numeroOuNulo = value =>
    value === null || value === undefined ? null : formatNumber(value)

  // Movimentações por QUEBRA (a OP quebrada): a quebra é a unidade real de
  // produção — tem status, quantidade, início e fim próprios (a ordem não tem
  // início preenchido, por isso o tempo aparecia vazio quando era por ordem).
  const columns = [
    { id: 'break', label: 'Quebra', element: row => row.numberBreak || '—' },
    { id: 'product', label: 'Produto', element: row => row.productName || '—' },
    { id: 'process', label: 'Processo', element: row => row.currentStageName || '—' },
    {
      id: 'quantity',
      label: 'Quantidade',
      align: 'right',
      element: row => formatNumber(row.quantityInitial),
    },
    {
      id: 'status',
      label: 'Status',
      element: row => {
        const status = getOrderStatus(row.status)
        return <Chip size="small" label={row.statusLabel || status.label} color={status.color} />
      },
    },
    { id: 'startedAt', label: 'Iniciado', element: row => (row.startedAt ? formatDateTime(row.startedAt) : '—') },
    { id: 'finishedAt', label: 'Concluído', element: row => (row.finishedAt ? formatDateTime(row.finishedAt) : '—') },
    { id: 'tempo', label: 'Tempo de produção', element: row => tempoDeProducao(row) },
    { id: 'created', label: 'Criado', element: row => formatDateTime(row.createdAt) },
  ]

  return (
    <Box>
      <PageHeader
        title="Dashboard de Produção"
        subtitle="Visão consolidada das ordens de produção da filial selecionada."
        actions={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={!subCompanyId || loading}
            onClick={fetchSummary}
          >
            Atualizar
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => setSubCompanyId(event.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="De"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={event => setStartDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Até"
              InputLabelProps={{ shrink: true }}
              value={finishDate}
              onChange={event => setFinishDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Button
              fullWidth
              color="inherit"
              disabled={!comPeriodo}
              onClick={() => {
                setStartDate('')
                setFinishDate('')
              }}
            >
              Limpar período
            </Button>
          </Grid>
          {comPeriodo && (
            <Grid item xs={12}>
              {/*
                Cada indicador tem a sua data, e sem dizer isso o painel parece
                inconsistente: com período, "gerados" deixa de ser a soma de
                "produzidos" e "pendentes" — são janelas diferentes.
              */}
              <Typography variant="caption" color="text.secondary">
                {periodoAplicado} · quebras, produção e peças por etapa contam pela data do
                apontamento (a quebra entra pela produção no período, não pela abertura); etiquetas
                geradas, pela data de criação; tempo médio, pela conclusão da quebra.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {!subCompanyId ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Selecione uma filial para carregar os indicadores.
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <IndicatorCard
                label="Total de Quebras (OPs quebradas)"
                value={numeroOuNulo(orders.total)}
                icon={AssignmentIcon}
                color="#1976d2"
                loading={loading}
                motivo={motivos.totalOrders}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <IndicatorCard
                label="Quebras em andamento"
                value={numeroOuNulo(orders.inProgress)}
                icon={PlayCircleOutlineIcon}
                color="#ed6c02"
                loading={loading}
                motivo={motivos.ordersInProgress}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <IndicatorCard
                label="Quebras finalizadas"
                value={numeroOuNulo(orders.finished)}
                icon={CheckCircleOutlineIcon}
                color="#2e7d32"
                loading={loading}
                motivo={motivos.ordersFinished}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <IndicatorCard
                label="Quebras canceladas"
                value={numeroOuNulo(orders.cancelled)}
                icon={CancelOutlinedIcon}
                color="#d32f2f"
                loading={loading}
                motivo={motivos.ordersCancelled}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <IndicatorCard
                label="Total de RFIDs gerados"
                value={numeroOuNulo(rfid.generated)}
                icon={QrCode2Icon}
                color="#0288d1"
                loading={loading}
                motivo={motivos.rfidGenerated}
              />
            </Grid>
            {/* "Total de RFIDs impressos" ocultado: nada foi migrado para
                print_count, então o número reflete só o que esta API imprimiu
                (hoje 0) e confunde mais que ajuda. */}
            <Grid item xs={12} sm={6} md={3}>
              <IndicatorCard
                label="Total de RFIDs produzidos"
                value={numeroOuNulo(rfid.produced)}
                icon={PrecisionManufacturingIcon}
                color="#2e7d32"
                loading={loading}
                motivo={motivos.rfidProduced}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <IndicatorCard
                label="Total de RFIDs pendentes"
                value={numeroOuNulo(rfid.pending)}
                icon={PendingActionsIcon}
                color="#ed6c02"
                loading={loading}
                motivo={motivos.rfidPending}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <IndicatorCard
                label="Tempo médio de produção"
                value={
                  summary && summary.averageProductionMinutes !== null &&
                  summary.averageProductionMinutes !== undefined
                    ? formatDuration(summary.averageProductionMinutes)
                    : null
                }
                icon={TimerOutlinedIcon}
                color="#455a64"
                loading={loading}
                motivo={motivos.averageProductionMinutes}
              />
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {stageMetric === 'quebra'
                    ? 'Total de quebras em cada etapa'
                    : 'Total de peças em cada etapa'}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={stageMetric}
                  onChange={(event, value) => value && setStageMetric(value)}
                >
                  <ToggleButton value="peca">Por peça</ToggleButton>
                  <ToggleButton value="quebra">Por quebra</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stageData} margin={{ top: 24, right: 24, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="stage" tick={{ fontSize: 12 }} interval={0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <ChartTooltip formatter={value => formatNumber(value)} />
                    <Line
                      type="monotone"
                      dataKey="quantity"
                      name={stageMetric === 'quebra' ? 'Quebras' : 'Peças'}
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    >
                      <LabelList
                        dataKey="quantity"
                        position="top"
                        offset={10}
                        formatter={value => formatNumber(value)}
                        style={{ fontSize: 12, fill: '#0e2436', fontWeight: 600 }}
                      />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              {stagesIndisponiveis && (
                <Typography variant="caption" color="text.secondary">
                  {motivos.stages || 'Sem dados de agregação por etapa para esta filial.'}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Movimentações
          </Typography>
          <DataTable
            columns={columns}
            rows={movimentacoes}
            loading={loadingMov}
            page={movPage}
            rowsPerPage={movRowsPerPage}
            total={movTotal}
            onPageChange={(event, novaPagina) => setMovPage(novaPagina)}
            onRowsPerPageChange={event => {
              setMovRowsPerPage(parseInt(event.target.value, 10))
              setMovPage(0)
            }}
            rowKey={row => row.id}
            emptyMessage={
              comPeriodo
                ? 'Nenhuma quebra com produção no período selecionado.'
                : 'Nenhuma quebra encontrada para a filial selecionada.'
            }
          />
        </>
      )}

      <LoadingBackdrop open={loading && !summary} message="Carregando indicadores..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default Dashboard
